/**
 * Reset Password Screen
 * 
 * Handles password reset deep links from Firebase password reset emails.
 * Parses oobCode from URL, verifies it, and allows user to set new password.
 * 
 * Firebase password reset URLs format:
 * https://app.lore.co?mode=resetPassword&oobCode=xxx
 * 
 * Or custom route:
 * https://app.lore.co/reset-password?oobCode=xxx
 * 
 * @module components/auth/ResetPasswordScreen
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Spinner } from '@sequelae/lore-core';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { getFirebaseAuth } from '@/config/firebase';
import { getSignUpPasswordError, isValidSignUpPassword } from '@/utils/passwordValidation';
import './ResetPasswordScreen.css';

type ScreenState = 'verifying' | 'ready' | 'success' | 'error';

export const ResetPasswordScreen: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Extract oobCode from URL
  const oobCode = searchParams.get('oobCode') || '';

  // Screen state
  const [screenState, setScreenState] = useState<ScreenState>('verifying');
  const [email, setEmail] = useState('');
  const [verifyError, setVerifyError] = useState('');

  // Form state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Result state
  const [submitError, setSubmitError] = useState('');

  /**
   * Verify the reset code on component mount
   */
  useEffect(() => {
    const verifyCode = async () => {
      if (!oobCode) {
        setVerifyError('No reset code provided. Please use the link from your email.');
        setScreenState('error');
        return;
      }

      try {
        console.log('[ResetPassword] Verifying oobCode...');
        const auth = getFirebaseAuth();
        const userEmail = await verifyPasswordResetCode(auth, oobCode);
        
        console.log('[ResetPassword] Code verified for email:', userEmail);
        setEmail(userEmail);
        setScreenState('ready');
      } catch (error: any) {
        console.error('[ResetPassword] Code verification failed:', error);
        
        if (error.code === 'auth/expired-action-code') {
          setVerifyError('This password reset link has expired. Please request a new one.');
        } else if (error.code === 'auth/invalid-action-code') {
          setVerifyError('This password reset link is invalid. It may have already been used.');
        } else {
          setVerifyError('Failed to verify reset link. Please try requesting a new one.');
        }
        setScreenState('error');
      }
    };

    verifyCode();
  }, [oobCode]);

  /**
   * Handle new password input change
   */
  const handleNewPasswordChange = (value: string) => {
    setNewPassword(value);
    const error = getSignUpPasswordError(value);
    setPasswordError(error);
    // Update confirm error if confirm password is filled
    if (confirmPassword && value !== confirmPassword) {
      setConfirmError('Passwords do not match.');
    } else if (confirmPassword && value === confirmPassword) {
      setConfirmError('');
    }
    // Clear submit error when user starts typing
    if (submitError) {
      setSubmitError('');
    }
  };

  /**
   * Handle confirm password input change
   */
  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    if (value && newPassword !== value) {
      setConfirmError('Passwords do not match.');
    } else {
      setConfirmError('');
    }
    // Clear submit error when user starts typing
    if (submitError) {
      setSubmitError('');
    }
  };

  /**
   * Check if form is valid for submission
   */
  const isFormValid = useCallback((): boolean => {
    return (
      isValidSignUpPassword(newPassword) &&
      newPassword === confirmPassword &&
      !isSubmitting
    );
  }, [newPassword, confirmPassword, isSubmitting]);

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      console.log('[ResetPassword] Confirming password reset...');
      
      const auth = getFirebaseAuth();
      await confirmPasswordReset(auth, oobCode, newPassword);
      
      console.log('[ResetPassword] Password reset successful');
      setScreenState('success');
      
    } catch (error: any) {
      console.error('[ResetPassword] Password reset failed:', error);
      
      if (error.code === 'auth/expired-action-code') {
        setSubmitError('This password reset link has expired. Please request a new one.');
      } else if (error.code === 'auth/invalid-action-code') {
        setSubmitError('This password reset link is no longer valid. Please request a new one.');
      } else if (error.code === 'auth/weak-password') {
        setSubmitError('Password is too weak. Please choose a stronger password.');
      } else {
        setSubmitError('Failed to reset password. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle navigation to sign in
   */
  const handleGoToSignIn = () => {
    navigate('/sign-in');
  };

  /**
   * Handle navigation to forgot password (request new link)
   */
  const handleRequestNewLink = () => {
    navigate('/forgot-password');
  };

  // Render verifying state
  if (screenState === 'verifying') {
    return (
      <div className="reset-password-screen">
        <div className="reset-password-card">
          <div className="reset-password-loading">
            <Spinner />
            <p className="reset-password-loading-text">Verifying reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  // Render error state (invalid/expired code)
  if (screenState === 'error') {
    return (
      <div className="reset-password-screen">
        <div className="reset-password-card">
          <header className="reset-password-header">
            <h1 className="reset-password-title">Reset Password</h1>
          </header>

          <div className="reset-password-error">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            <div className="reset-password-error-content">
              <p className="reset-password-error-title">Link Invalid</p>
              <p className="reset-password-error-message">
                {verifyError}
                {' '}If you continue to have issues, please contact us at{' '}
                <a href="mailto:hello@lore.co" className="reset-password-support-link">
                  hello@lore.co
                </a>
              </p>
            </div>
          </div>

          <div style={{ marginTop: '24px' }}>
            <Button
              type="button"
              variant="primary"
              size="large"
              fullWidth
              onClick={handleRequestNewLink}
            >
              Request New Link
            </Button>

            <button
              type="button"
              className="reset-password-back-link"
              onClick={handleGoToSignIn}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render success state
  if (screenState === 'success') {
    return (
      <div className="reset-password-screen">
        <div className="reset-password-card">
          <header className="reset-password-header">
            <h1 className="reset-password-title">Password Reset</h1>
          </header>

          <div className="reset-password-success">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
            <div className="reset-password-success-content">
              <p className="reset-password-success-title">Password Reset Successful!</p>
              <p className="reset-password-success-message">
                Your password has been reset. You can now sign in with your new password.
              </p>
            </div>
          </div>

          <div style={{ marginTop: '24px' }}>
            <Button
              type="button"
              variant="primary"
              size="large"
              fullWidth
              onClick={handleGoToSignIn}
            >
              Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Render ready state (form)
  return (
    <div className="reset-password-screen">
      <div className="reset-password-card">
        {/* Header */}
        <header className="reset-password-header">
          <h1 className="reset-password-title">Reset Password</h1>
          <button 
            className="reset-password-close-btn" 
            onClick={handleGoToSignIn}
            aria-label="Close"
            disabled={isSubmitting}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </header>

        {/* Subtitle */}
        <p className="reset-password-subtitle">
          Enter your new password below.
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="reset-password-form">
          {/* Email Display (readonly) */}
          <div className="reset-password-field-group">
            <label className="reset-password-field-label">
              Email Address
            </label>
            <div className="reset-password-email-display">
              {email}
            </div>
          </div>

          {/* New Password Field */}
          <div className="reset-password-field-group">
            <label htmlFor="new-password" className="reset-password-field-label">
              New Password
            </label>
            <div className="reset-password-input-container">
              <input
                id="new-password"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => handleNewPasswordChange(e.target.value)}
                placeholder="Enter new password"
                disabled={isSubmitting}
                className={`reset-password-field-input ${passwordError ? 'has-error' : ''}`}
                autoComplete="new-password"
                autoFocus
              />
              <button
                type="button"
                className="reset-password-visibility-toggle"
                onClick={() => setShowNewPassword(!showNewPassword)}
                aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                disabled={isSubmitting}
              >
                {showNewPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            {passwordError && <p className="reset-password-field-error">{passwordError}</p>}
          </div>

          {/* Confirm Password Field */}
          <div className="reset-password-field-group">
            <label htmlFor="confirm-password" className="reset-password-field-label">
              Confirm Password
            </label>
            <div className="reset-password-input-container">
              <input
                id="confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                placeholder="Confirm new password"
                disabled={isSubmitting}
                className={`reset-password-field-input ${confirmError ? 'has-error' : ''}`}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="reset-password-visibility-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                disabled={isSubmitting}
              >
                {showConfirmPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            {confirmError && <p className="reset-password-field-error">{confirmError}</p>}
          </div>

          {/* Submit Error */}
          {submitError && (
            <div className="reset-password-error">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
              <div className="reset-password-error-content">
                <p className="reset-password-error-title">Reset Failed</p>
                <p className="reset-password-error-message">
                  {submitError}
                  {' '}If you continue to have issues, please contact us at{' '}
                  <a href="mailto:hello@lore.co" className="reset-password-support-link">
                    hello@lore.co
                  </a>
                </p>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            size="large"
            fullWidth
            disabled={!isFormValid() || isSubmitting}
          >
            {isSubmitting ? <Spinner /> : 'Reset Password'}
          </Button>

          {/* Back to Sign In Link */}
          <button
            type="button"
            className="reset-password-back-link"
            onClick={handleGoToSignIn}
            disabled={isSubmitting}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back to Sign In
          </button>
        </form>
      </div>
    </div>
  );
};

